import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { HoldingComplex, HoldingRoom } from './types';
import { useCrowd } from './context/CrowdContext';
import { fmt } from './utils/crowdMath';
import {
  ArrowRight,
  Camera,
  DoorOpen,
  Eye,
  Flame,
  Info,
  Move,
  Radio,
  RotateCcw,
  Ruler,
  Warehouse,
  X,
  ZoomIn,
  ZoomOut
} from 'lucide-react';

interface HoldingAreaBlueprintModalProps {
  holdingArea: HoldingComplex;
  onClose?: () => void;
  onOpenCamera: (camId: string, roomName: string, count: number, cap: number) => void;
  onUpdateRoomGate?: (holdingAreaId: string, roomId: string, status: 'open' | 'metered' | 'closed') => void;
  onFlushRoom?: (holdingAreaId: string, roomId: string) => void;
  isInline?: boolean;
  selectedAreaId?: string;
  onAreaChange?: (id: string) => void;
}

interface PlotRoom {
  room: HoldingRoom;
  x: number;
  y: number;
  w: number;
  h: number;
}

const occupancy = (room: HoldingRoom) => Math.round((room.currentCount / Math.max(1, room.capacity)) * 100);

function layoutRooms(area: HoldingComplex): PlotRoom[] {
  if (area.id === 'HA-1') {
    // Site map of 2026-10-08: seven columns, numbered down each column. Exit at the top-left, entry at the bottom-right.
    const columns = [[1, 2, 3, 4, 5], [6, 7, 8, 9, 10], [11, 12, 13, 14, 15], [16, 17, 18, 19], [20, 21, 22, 23], [24, 25, 26, 27], [28, 29, 30]];
    return columns.flatMap((blocks, col) => blocks.map((number, row) => {
      const room = area.rooms[number - 1];
      const short = room.dimensions === "20'x20'";
      const medium = room.dimensions === "30'x20'";
      return { room, x: 215 + col * 138, y: 108 + row * 100, w: 114, h: short ? 64 : medium ? 78 : 88 };
    }));
  }

  if (area.id === 'HA-2') {
    // Site plan of 15 Oct 2026 (H-2): entry at the top (camera 192.168.2.170); boxes 1-8 in pairs on the right
    // (1|2, 3|4, 5|6, 7|8, odd on the left); boxes 9-17 in one row along the bottom, 9 at the right and 17 at the
    // left, next to the exit (192.168.3.56). Cameras per box as in backend config/holding.json.
    const byNum = (n: number) => area.rooms[n - 1];
    const pairs = [[1, 2], [3, 4], [5, 6], [7, 8]].flatMap(([l, r], row) => [
      { room: byNum(l), x: 640, y: 124 + row * 84, w: 300, h: 72 },
      { room: byNum(r), x: 954, y: 124 + row * 84, w: 300, h: 72 },
    ]);
    // the exit sits beside box 17, clear of the facilities / release cards drawn over the bottom corners
    const bottom = [17, 16, 15, 14, 13, 12, 11, 10, 9].map((n, i) => ({ room: byNum(n), x: 112 + i * 130, y: 484, w: 118, h: 104 }));
    return [...pairs, ...bottom].filter(p => p.room);
  }

  const columnSizes = [9, 9, 8, 7];
  let offset = 0;
  return columnSizes.flatMap((size, col) => {
    const rooms = area.rooms.slice(offset, offset + size);
    offset += size;
    return rooms.map((room, row) => ({
      room,
      x: 145 + col * 285,
      y: 70 + row * 70,
      w: 176,
      h: room.dimensions === "35'x30'" ? 82 : 60
    }));
  });
}

export const HoldingAreaBlueprintModal: React.FC<HoldingAreaBlueprintModalProps> = ({
  holdingArea,
  onClose,
  onOpenCamera,
  onFlushRoom,
  isInline = false,
  selectedAreaId,
  onAreaChange
}) => {
  const { themeMode, holdingComplexes } = useCrowd();
  const isLight = themeMode === 'light';
  const areas = useMemo(
    () => holdingComplexes.filter(area => ['HA-1', 'HA-2', 'HA-3'].includes(area.id)),
    [holdingComplexes]
  );
  const activeArea = areas.find(area => area.id === selectedAreaId) || areas.find(area => area.id === holdingArea.id) || holdingArea;
  const plotted = useMemo(() => layoutRooms(activeArea), [activeArea]);
  const [selectedRoomId, setSelectedRoomId] = useState<string | null>(null);
  const [showCameras, setShowCameras] = useState(true);
  const [showFlow, setShowFlow] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });

  useEffect(() => {
    setSelectedRoomId(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, [activeArea.id]);

  useEffect(() => {
    if (isInline || !onClose) return;
    const close = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [isInline, onClose]);

  const areaStats = useMemo(() => areas.map(area => {
    const people = area.rooms.reduce((sum, room) => sum + room.currentCount, 0);
    // Wait = people in the boxes / people released per minute at the area's exit camera. Without a counting
    // exit camera (or with nobody being released) there is no honest figure: null, shown as "—".
    const rate = area.releasedPerMin;
    const wait: number | null = people === 0 ? 0 : rate != null && rate >= 0.2 ? Math.max(1, Math.ceil(people / rate)) : null;
    return { area, people, wait, pct: Math.round((people / Math.max(1, area.totalCapacity)) * 100) };
  }), [areas]);

  const activeStats = areaStats.find(stat => stat.area.id === activeArea.id);
  // recommendations only use boxes that are measured (live === false = no camera counting there yet)
  const rankedForRelease = [...activeArea.rooms]
    .filter(room => room.gateStatus !== 'closed' && room.live !== false)
    .sort((a, b) => occupancy(b) - occupancy(a));
  const rankedForFill = [...activeArea.rooms]
    .filter(room => room.gateStatus !== 'closed' && room.live !== false)
    .sort((a, b) => occupancy(a) - occupancy(b));
  const releaseRoom = rankedForRelease[0];
  const fillRoom = rankedForFill.find(room => room.id !== releaseRoom?.id) || rankedForFill[0];
  const selectedRoom = activeArea.rooms.find(room => room.id === selectedRoomId) || null;

  const colors = (room: HoldingRoom, selected: boolean) => {
    const pct = occupancy(room);
    if (selected) return { fill: isLight ? '#dbeafe' : '#102b48', stroke: '#3b82f6', text: isLight ? '#1e3a8a' : '#dbeafe' };
    if (pct > 80) return { fill: isLight ? '#fff1f2' : '#35151d', stroke: '#f43f5e', text: isLight ? '#9f1239' : '#fecdd3' };
    if (pct > 50) return { fill: isLight ? '#fffbeb' : '#32270b', stroke: '#f59e0b', text: isLight ? '#92400e' : '#fde68a' };
    return { fill: isLight ? '#ecfdf5' : '#082a22', stroke: '#10b981', text: isLight ? '#065f46' : '#a7f3d0' };
  };

  const chooseArea = (id: string) => onAreaChange?.(id);
  const resetView = () => { setZoom(1); setPan({ x: 0, y: 0 }); };
  const onMouseDown = (event: React.MouseEvent) => {
    if (event.button !== 0) return;
    setDragging(true);
    dragStart.current = { x: event.clientX - pan.x, y: event.clientY - pan.y };
  };
  const onMouseMove = (event: React.MouseEvent) => {
    if (!dragging) return;
    setPan({ x: event.clientX - dragStart.current.x, y: event.clientY - dragStart.current.y });
  };

  const content = (
    <section className={`relative w-full ${isInline ? 'rounded-2xl border border-neutral-200/80 dark:border-[#273141]' : 'max-w-[1540px] h-[94vh] rounded-2xl border border-white/10'} bg-white dark:bg-[#111821] overflow-hidden shadow-sm flex flex-col`}>
      {!isInline && onClose && (
        <button onClick={onClose} aria-label="Close blueprint" className="absolute right-3 top-3 z-30 p-2 rounded-lg bg-white/90 dark:bg-[#172231] border border-neutral-200 dark:border-white/10 text-neutral-500 hover:text-neutral-900 dark:hover:text-white">
          <X className="w-4 h-4" />
        </button>
      )}

      <header className={`shrink-0 p-3 sm:p-4 ${!isInline ? 'pr-14' : ''} border-b border-neutral-200/80 dark:border-white/8 bg-white dark:bg-[#121a25]`}>
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-lg shadow-blue-600/20 shrink-0">
              <Warehouse className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg sm:text-xl font-black text-neutral-950 dark:text-white">Holding Area Digital Twin</h2>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 px-2 py-0.5 text-xs font-semibold">
                  <Radio className="w-3 h-3 animate-pulse" /> Live operations
                </span>
              </div>
              <p className="text-xs sm:text-[13px] text-neutral-500 dark:text-slate-400 mt-0.5">Per-box capacity uses the supplied dimensions at 4 sq ft per seated devotee.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 min-w-0 xl:min-w-[660px]">
            {areaStats.map(({ area, people, wait, pct }) => {
              const active = area.id === activeArea.id;
              return (
                <button key={area.id} onClick={() => chooseArea(area.id)} className={`text-left rounded-xl border px-3 py-2 transition ${active ? 'bg-blue-600 border-blue-600 text-white shadow-lg shadow-blue-600/15' : 'bg-neutral-50 dark:bg-white/[0.03] border-neutral-200 dark:border-white/10 hover:border-blue-400'}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`text-xs font-extrabold ${active ? 'text-white' : 'text-neutral-900 dark:text-white'}`}>{area.id}</span>
                    <span className={`text-xs font-bold ${active ? 'text-blue-100' : pct > 80 ? 'text-rose-500' : pct > 50 ? 'text-amber-500' : 'text-emerald-500'}`}>{pct}%</span>
                  </div>
                  <div className={`mt-1 text-sm sm:text-base font-bold ${active ? 'text-white' : 'text-neutral-900 dark:text-slate-100'}`}>{fmt(people)} / {fmt(area.totalCapacity)}</div>
                  <div className={`mt-0.5 text-xs ${active ? 'text-blue-100' : 'text-neutral-500 dark:text-slate-400'}`}>{area.rooms.length} boxes{wait == null ? '' : wait === 0 ? ' · no wait' : ` · ~${wait} min wait`}</div>
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <div className="shrink-0 grid grid-cols-1 md:grid-cols-[1.35fr_1fr_1fr] gap-2 p-3 border-b border-neutral-200/80 dark:border-white/8 bg-neutral-50/80 dark:bg-[#0d141e]">
        <div className="rounded-xl border border-neutral-200 dark:border-white/10 bg-white dark:bg-white/[0.03] p-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 dark:text-slate-400">{activeArea.id} live load</div>
            <div className="flex items-baseline gap-2 mt-0.5">
              <span className="text-xl sm:text-2xl font-black text-neutral-950 dark:text-white">{fmt(activeStats?.people || 0)}</span>
              <span className="text-xs text-neutral-500">of {fmt(activeArea.totalCapacity)} calculated seats</span>
            </div>
            <div className="text-xs text-neutral-400 mt-0.5">Drawing label: {fmt(activeArea.declaredCapacity)}{activeStats?.wait == null ? ' · wait not measured (exit camera not counting)' : ` · estimated wait: ${activeStats.wait} min`}</div>
          </div>
          <div className="w-16 h-16 rounded-full grid place-items-center shrink-0" style={{ background: `conic-gradient(#2563eb ${(activeStats?.pct || 0) * 3.6}deg, ${isLight ? '#e5e7eb' : '#273244'} 0)` }}>
            <div className="w-12 h-12 rounded-full bg-white dark:bg-[#111821] grid place-items-center text-sm font-extrabold">{activeStats?.pct || 0}%</div>
          </div>
        </div>

        <div className="rounded-xl border border-orange-500/25 bg-orange-500/[0.06] p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-orange-500 text-white grid place-items-center shrink-0"><ArrowRight className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-orange-600 dark:text-orange-400">Release next</div>
            <div className="text-base font-extrabold text-neutral-950 dark:text-white truncate">Box {releaseRoom?.blueprintNumber || '—'} · {releaseRoom ? occupancy(releaseRoom) : 0}%</div>
            <div className="text-xs text-neutral-500 dark:text-slate-400 truncate">{activeArea.releaseCorridor}</div>
          </div>
        </div>

        <div className="rounded-xl border border-cyan-500/25 bg-cyan-500/[0.06] p-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-500 text-white grid place-items-center shrink-0"><DoorOpen className="w-4 h-4" /></div>
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wider font-bold text-cyan-600 dark:text-cyan-400">Fill next</div>
            <div className="text-base font-extrabold text-neutral-950 dark:text-white truncate">Box {fillRoom?.blueprintNumber || '—'} · {fillRoom ? fmt(fillRoom.capacity - fillRoom.currentCount) : 0} seats free</div>
            <div className="text-xs text-neutral-500 dark:text-slate-400">Police routing recommendation</div>
          </div>
        </div>
      </div>

      <div className="shrink-0 px-3 py-2 flex items-center justify-between gap-2 border-b border-neutral-200/80 dark:border-white/8 bg-white dark:bg-[#111821]">
        <div className="flex items-center gap-1.5 flex-wrap">
          <button onClick={() => setShowCameras(value => !value)} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 cursor-pointer ${showCameras ? 'bg-blue-500/10 border-blue-500/30 text-blue-600 dark:text-blue-400' : 'border-neutral-200 dark:border-white/10 text-neutral-500'}`}><Camera className="w-3.5 h-3.5" /> CCTV</button>
          <button onClick={() => setShowFlow(value => !value)} className={`px-2.5 py-1.5 rounded-lg text-xs font-semibold border flex items-center gap-1.5 cursor-pointer ${showFlow ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-600 dark:text-cyan-400' : 'border-neutral-200 dark:border-white/10 text-neutral-500'}`}><Eye className="w-3.5 h-3.5" /> Flow paths</button>
          <span className="hidden sm:inline-flex items-center gap-1 text-xs text-neutral-400 ml-1"><Ruler className="w-3 h-3" /> Seat capacity = floor(box area ÷ 4 sq ft)</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-neutral-400 font-mono mr-1">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(value => Math.min(2.2, value + 0.15))} className="p-1.5 rounded-lg border border-neutral-200 dark:border-white/10 text-neutral-500 cursor-pointer" aria-label="Zoom in"><ZoomIn className="w-3.5 h-3.5" /></button>
          <button onClick={() => setZoom(value => Math.max(0.7, value - 0.15))} className="p-1.5 rounded-lg border border-neutral-200 dark:border-white/10 text-neutral-500 cursor-pointer" aria-label="Zoom out"><ZoomOut className="w-3.5 h-3.5" /></button>
          <button onClick={resetView} className="p-1.5 rounded-lg border border-neutral-200 dark:border-white/10 text-neutral-500 cursor-pointer" aria-label="Reset view"><RotateCcw className="w-3.5 h-3.5" /></button>
        </div>
      </div>

      <div
        className={`${isInline ? 'aspect-[1320/720] min-h-[480px]' : 'flex-1 min-h-0'} relative overflow-hidden cursor-grab active:cursor-grabbing bg-[#f8fafc] dark:bg-[#061019]`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={() => setDragging(false)}
        onMouseLeave={() => setDragging(false)}
      >
        <div className="w-full h-full will-change-transform" style={{ transform: `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`, transformOrigin: 'center' }}>
          <svg viewBox="0 0 1320 720" className="w-full h-full" role="img" aria-label={`${activeArea.id} live holding area blueprint`}>
            <defs>
              <pattern id="holding-grid" width="24" height="24" patternUnits="userSpaceOnUse"><path d="M 24 0 L 0 0 0 24" fill="none" stroke={isLight ? '#e9eef5' : '#102434'} strokeWidth="1" /></pattern>
              <marker id="flow-arrow" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill="#06b6d4" /></marker>
              <filter id="room-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="3" stdDeviation="3" floodOpacity={isLight ? '0.12' : '0.3'} /></filter>
            </defs>
            <rect width="1320" height="720" fill={isLight ? '#f8fafc' : '#061019'} />
            <rect width="1320" height="720" fill="url(#holding-grid)" />

            <g>
              <text x="54" y="42" fill={isLight ? '#0f172a' : '#f8fafc'} fontSize="20" fontWeight="800">{activeArea.name}</text>
              <text x="54" y="63" fill={isLight ? '#64748b' : '#94a3b8'} fontSize="12">{activeArea.location} · {activeArea.sector}</text>
              <rect x="1135" y="24" width="130" height="44" rx="10" fill={isLight ? '#ffffff' : '#0d1a26'} stroke={isLight ? '#cbd5e1' : '#274055'} />
              <text x="1200" y="42" textAnchor="middle" fill={isLight ? '#64748b' : '#94a3b8'} fontSize="10" fontWeight="700">WAIT TO Q-LINE</text>
              <text x="1200" y="59" textAnchor="middle" fill="#2563eb" fontSize="15" fontWeight="800">{activeStats?.wait == null ? '—' : `~${activeStats.wait} MIN`}</text>
            </g>

            {activeArea.id === 'HA-1' && (() => {
              const ink = isLight ? '#475569' : '#94a3b8'; const faint = isLight ? '#cbd5e1' : '#274055';
              const exitC = '#ef4444'; const inC = '#06b6d4';
              return (
                <g fontWeight="700">
                  <defs>
                    <marker id="ha1-out" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill={exitC} /></marker>
                    <marker id="ha1-in" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill={inC} /></marker>
                  </defs>
                  {/* exit lane along the north side, everybody leaves to the north-west */}
                  <text x="40" y="92" fill={exitC} fontSize="13" fontWeight="800">HOLDING AREA EXIT</text>
                  {showFlow && <path d="M 1180 96 L 190 96" fill="none" stroke={exitC} strokeWidth="2.5" strokeDasharray="9 7" markerEnd="url(#ha1-out)" />}
                  {[0, 1, 2, 3, 4, 5, 6].map(c => <text key={c} x={215 + c * 138 + 57} y="88" textAnchor="middle" fill={exitC} fontSize="8">EXIT</text>)}
                  <text x="1188" y="92" fill={exitC} fontSize="8">EMERGENCY EXIT</text>
                  {/* west side: gangway, emergency exits, toilets */}
                  <rect x="30" y="330" width="78" height="250" rx="6" fill="none" stroke={faint} strokeWidth="1.5" />
                  <text x="69" y="455" textAnchor="middle" fill={ink} fontSize="15" fontWeight="800" transform="rotate(-90 69 455)">TOILETS</text>
                  <text x="182" y="230" textAnchor="middle" fill={ink} fontSize="10" transform="rotate(-90 182 230)">GANG WAY</text>
                  <text x="182" y="500" textAnchor="middle" fill={ink} fontSize="10" transform="rotate(-90 182 500)">GANG WAY</text>
                  <text x="150" y="340" textAnchor="middle" fill={exitC} fontSize="8" transform="rotate(-90 150 340)">EMERGENCY EXIT</text>
                  <text x="150" y="600" textAnchor="middle" fill={exitC} fontSize="8" transform="rotate(-90 150 600)">EMERGENCY EXIT</text>
                  {showFlow && <path d="M 196 590 L 196 110" fill="none" stroke={exitC} strokeWidth="1.5" strokeDasharray="6 6" markerEnd="url(#ha1-out)" opacity="0.7" />}
                  {/* gangways between the columns: people walk north to the exit lane */}
                  {showFlow && [0, 1, 2, 3, 4, 5].map(c => (
                    <path key={c} d={`M ${215 + c * 138 + 126} 590 L ${215 + c * 138 + 126} 110`} fill="none" stroke={exitC} strokeWidth="1.2" strokeDasharray="5 7" markerEnd="url(#ha1-out)" opacity="0.55" />
                  ))}
                  {/* east side: entry gangway and the temporary barricading */}
                  <text x="1196" y="230" textAnchor="middle" fill={ink} fontSize="10" transform="rotate(-90 1196 230)">GANG WAY</text>
                  <text x="1196" y="470" textAnchor="middle" fill={ink} fontSize="10" transform="rotate(-90 1196 470)">GANG WAY</text>
                  <path d="M 1238 100 L 1226 600" fill="none" stroke={faint} strokeWidth="2" strokeDasharray="3 5" />
                  <text x="1262" y="350" textAnchor="middle" fill={ink} fontSize="11" transform="rotate(-82 1262 350)">TEMPORARY BARRICADING</text>
                  {showFlow && <path d="M 1214 598 L 1214 130" fill="none" stroke={inC} strokeWidth="2.5" strokeDasharray="9 7" markerEnd="url(#ha1-in)" />}
                  {[0, 1, 2].map(r => <text key={r} x="1164" y={108 + r * 100 + 82} fill={inC} fontSize="8">◄ ENTRY</text>)}
                  {/* south side: entrance at the south-east corner, gangway, way to the toilets */}
                  <rect x="1222" y="604" width="26" height="40" rx="3" fill="none" stroke={inC} strokeWidth="1.5" />
                  <text x="1235" y="628" textAnchor="middle" fill={inC} fontSize="7" transform="rotate(-90 1235 628)">STEPS</text>
                  <text x="1256" y="662" textAnchor="end" fill={inC} fontSize="12" fontWeight="800">HOLDING AREA ENTRANCE</text>
                  <text x="760" y="622" textAnchor="middle" fill={ink} fontSize="11">GANG WAY</text>
                  {showFlow && <path d="M 1216 640 L 700 640 L 520 668 L 130 668" fill="none" stroke={ink} strokeWidth="1.5" strokeDasharray="6 6" markerEnd="url(#flow-arrow)" opacity="0.8" />}
                  <text x="330" y="684" fill={ink} fontSize="9">WAY TO TOILETS</text>
                  {/* each block: entry at its south-east corner, exit at its north-west corner */}
                  {plotted.map(({ room, x, y, w, h }) => (
                    <g key={`io-${room.id}`} fontSize="6.5">
                      <text x={x + 2} y={y - 2.5} fill={exitC}>EXIT</text>
                      <text x={x + w - 2} y={y + h + 8} textAnchor="end" fill={inC}>ENTRY</text>
                    </g>
                  ))}
                  {/* north arrow and the drawing's own totals */}
                  <g transform="translate(70 640)" stroke={ink} fill="none" strokeWidth="1.2">
                    <circle r="9" /><path d="M 0 -22 L 5 -9 L -5 -9 Z" fill={ink} /><path d="M 0 22 L 0 9 M -22 0 L -9 0 M 22 0 L 9 0" />
                    <text y="-25" textAnchor="middle" fill={ink} stroke="none" fontSize="9">N</text>
                  </g>
                  <text x="1216" y="706" textAnchor="end" fill={ink} fontSize="9.5" fontWeight="600">30 blocks: 27 of 40′×20′, 3 of 30′×20′ · layout as per the site map of 8 Oct 2026</text>
                </g>
              );
            })()}

            {activeArea.id === 'HA-2' && (() => {
              const ink = isLight ? '#475569' : '#94a3b8'; const faint = isLight ? '#cbd5e1' : '#274055';
              const exitC = '#ef4444'; const inC = '#06b6d4';
              return (
                <g fontWeight="700">
                  <defs>
                    <marker id="ha2-out" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill={exitC} /></marker>
                    <marker id="ha2-in" viewBox="0 0 10 10" refX="8" refY="5" markerWidth="5" markerHeight="5" orient="auto-start-reverse"><path d="M 0 0 L 10 5 L 0 10 z" fill={inC} /></marker>
                  </defs>
                  {/* north wall with the entrance gap (camera 192.168.2.170) */}
                  <path d="M 54 104 L 470 104 M 610 104 L 1270 104" fill="none" stroke={faint} strokeWidth="3" />
                  <path d="M 470 104 L 470 84 M 610 104 L 610 84" fill="none" stroke={inC} strokeWidth="2.5" />
                  <text x="540" y="96" textAnchor="middle" fill={inC} fontSize="13" fontWeight="800">ENTRY</text>
                  <text x="540" y="120" textAnchor="middle" fill={inC} fontSize="9">camera 2.170</text>
                  {showFlow && <path d="M 540 126 L 540 462 L 1180 462" fill="none" stroke={inC} strokeWidth="2.5" strokeDasharray="9 7" markerEnd="url(#ha2-in)" />}
                  <text x="350" y="300" textAnchor="middle" fill={ink} fontSize="11">GANG WAY</text>
                  <text x="760" y="476" textAnchor="middle" fill={ink} fontSize="10">GANG WAY</text>
                  {/* exit beside box 17 at the west end of the bottom row (camera 192.168.3.56) */}
                  <path d="M 100 484 L 100 588" fill="none" stroke={faint} strokeWidth="3" />
                  <text x="62" y="530" textAnchor="middle" fill={exitC} fontSize="13" fontWeight="800">EXIT</text>
                  <text x="62" y="546" textAnchor="middle" fill={exitC} fontSize="9">camera 3.56</text>
                  {showFlow && <path d="M 1180 604 L 108 604 L 108 566 L 34 566" fill="none" stroke={exitC} strokeWidth="2.5" strokeDasharray="9 7" markerEnd="url(#ha2-out)" />}
                  <text x="1254" y="706" textAnchor="end" fill={ink} fontSize="9.5" fontWeight="600">17 boxes: 14 of 30′×25′, 3 of 40′×25′ · layout as per the site plan of 15 Oct 2026</text>
                </g>
              );
            })()}

            {showFlow && activeArea.id !== 'HA-1' && activeArea.id !== 'HA-2' && (
              <g opacity="0.9">
                <path d="M 38 360 C 82 360 84 330 110 330" fill="none" stroke="#06b6d4" strokeWidth="4" strokeDasharray="10 8" markerEnd="url(#flow-arrow)" />
                <path d="M 1160 350 C 1220 350 1240 390 1285 390" fill="none" stroke="#06b6d4" strokeWidth="4" strokeDasharray="10 8" markerEnd="url(#flow-arrow)" />
                <text x="38" y="340" fill="#0891b2" fontSize="12" fontWeight="800">ENTRY</text>
                <text x="1210" y="374" fill="#0891b2" fontSize="12" fontWeight="800">TO Q-LINE</text>
                <text x="590" y="694" textAnchor="middle" fill={isLight ? '#64748b' : '#64748b'} fontSize="12" fontWeight="700">EMERGENCY GANGWAY · KEEP CLEAR</text>
              </g>
            )}

            {plotted.map(({ room, x, y, w, h }) => {
              const selected = room.id === selectedRoomId;
              const tone = colors(room, selected);
              const pct = occupancy(room);
              const compact = h < 75;
              const narrow = w < 80;
              return (
                <g key={room.id} onClick={(event) => { event.stopPropagation(); setSelectedRoomId(room.id); }} className="cursor-pointer" filter={selected ? 'url(#room-shadow)' : undefined}>
                  <rect x={x} y={y} width={w} height={h} rx="7" fill={tone.fill} stroke={tone.stroke} strokeWidth={selected ? 3 : 1.5} />
                  <rect x={x + 1} y={y + 1} width={Math.max(0, (w - 2) * Math.min(100, pct) / 100)} height="5" rx="3" fill={tone.stroke} opacity="0.85" />
                  <text x={x + (narrow ? 6 : 10)} y={y + (compact ? 20 : 23)} fill={tone.text} fontSize={narrow ? 10 : 13} fontWeight="800">{narrow ? 'B' : 'BOX '}{room.blueprintNumber}</text>
                  <text x={x + (narrow ? 6 : 10)} y={y + (compact ? 35 : 40)} fill={isLight ? '#64748b' : '#94a3b8'} fontSize={narrow ? 8 : 10} fontWeight="600">{room.dimensions} · {room.capacity}</text>
                  <text x={x + (narrow ? 6 : 10)} y={y + h - (compact ? 7 : 12)} fill={tone.text} fontSize={narrow ? 11 : 14} fontWeight="800">{room.live === false ? '—' : room.currentCount}/{room.capacity}</text>
                  {!narrow && <text x={x + w - 9} y={y + h - (compact ? 7 : 12)} textAnchor="end" fill={tone.stroke} fontSize="11" fontWeight="800">{pct}%</text>}
                  {showCameras && (
                    <g onClick={(event) => { event.stopPropagation(); onOpenCamera(room.cams[0], `${activeArea.id} · Box ${room.blueprintNumber}`, room.currentCount, room.capacity); }}>
                      <circle cx={x + w - 13} cy={y + 15} r="8" fill={isLight ? '#fff' : '#111827'} stroke="#3b82f6" />
                      <circle cx={x + w - 13} cy={y + 15} r="3" fill="#3b82f6" />
                      <circle cx={x + w - 8} cy={y + 10} r="2" fill="#ef4444" />
                    </g>
                  )}
                </g>
              );
            })}

            <g display={activeArea.id === 'HA-1' ? 'none' : undefined}>
              <rect x="38" y="620" width="190" height="45" rx="8" fill={isLight ? '#fff' : '#0d1a26'} stroke={isLight ? '#cbd5e1' : '#274055'} />
              <text x="54" y="639" fill={isLight ? '#475569' : '#94a3b8'} fontSize="10" fontWeight="700">FACILITIES</text>
              <text x="54" y="655" fill={isLight ? '#0f172a' : '#e2e8f0'} fontSize="11" fontWeight="700">Medical · Water · Toilets · PA</text>
              <rect x="1090" y="620" width="190" height="45" rx="8" fill={isLight ? '#fff' : '#0d1a26'} stroke={isLight ? '#cbd5e1' : '#274055'} />
              <text x="1106" y="639" fill={isLight ? '#475569' : '#94a3b8'} fontSize="10" fontWeight="700">RELEASE CORRIDOR</text>
              <text x="1106" y="655" fill="#0891b2" fontSize="11" fontWeight="800">POLICE METERED · ONE-WAY</text>
            </g>
          </svg>
        </div>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white/90 dark:bg-[#0d1a26]/90 border border-neutral-200 dark:border-white/10 px-3 py-1 text-[11px] text-neutral-500 dark:text-slate-400 flex items-center gap-1.5 backdrop-blur"><Move className="w-3 h-3" /> Drag to pan · select any box to inspect</div>
      </div>

      <footer className="shrink-0 min-h-[60px] p-3 border-t border-neutral-200/80 dark:border-white/8 bg-white dark:bg-[#111821]">
        {selectedRoom ? (
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-extrabold shrink-0">{activeArea.id} · {selectedRoom.blueprintNumber}</div>
              <div className="min-w-0">
                <div className="text-sm font-bold text-neutral-950 dark:text-white truncate">{selectedRoom.name}</div>
                <div className="text-xs text-neutral-500 dark:text-slate-400">{fmt(selectedRoom.areaSqFt)} sq ft ÷ 4 = {fmt(selectedRoom.capacity)} seats · {fmt(selectedRoom.currentCount)} present · {occupancy(selectedRoom)}% · gate {selectedRoom.gateStatus}</div>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => onOpenCamera(selectedRoom.cams[0], `${activeArea.id} · Box ${selectedRoom.blueprintNumber}`, selectedRoom.currentCount, selectedRoom.capacity)} className="px-3 py-2 rounded-lg bg-blue-500/10 border border-blue-500/25 text-blue-600 dark:text-blue-400 text-xs font-bold flex items-center gap-1.5"><Camera className="w-3.5 h-3.5" /> Open AI CCTV</button>
              {onFlushRoom && <button onClick={() => onFlushRoom(activeArea.id, selectedRoom.id)} className="px-3 py-2 rounded-lg bg-orange-500 text-white text-xs font-bold flex items-center gap-1.5"><Flame className="w-3.5 h-3.5" /> Release box</button>}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 text-xs text-neutral-500 dark:text-slate-400">
            <span className="flex items-center gap-2"><Info className="w-4 h-4 text-blue-500" /> Select a box for live count, capacity math, CCTV and release controls.</span>
            <span className="hidden md:inline font-semibold">Green ≤50% · Amber 51–80% · Red &gt;80%</span>
          </div>
        )}
      </footer>
    </section>
  );

  if (isInline) return content;
  return <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-3 flex items-center justify-center">{content}</div>;
};
